import { useEffect, useRef } from "react";
import { useAppStore } from "../stores/appStore";
import useChatStore from "../stores/chatStore";

interface useInitialMessagesProps {
  messageTheme: string | null;
  targetMessageId: string | null;
  messagesContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  previousMessageThemeRef: React.MutableRefObject<string | null>;
  initialLoadCompletedRef: React.MutableRefObject<boolean>;
  setIsSearchingMessage: (value: boolean) => void;
  setTargetMessageId: (value: null) => void;
  cleanMessageArray: () => Promise<void>;
  scrollToBottomInitial: (container: HTMLDivElement | null) => void;
  themeChangeFromMessageRef: React.MutableRefObject<boolean>;
}

export const useInitialMessages = ({
  messageTheme,
  targetMessageId,
  messagesContainerRef,
  previousMessageThemeRef,
  initialLoadCompletedRef,
  setIsSearchingMessage,
  setTargetMessageId,
  cleanMessageArray,
  scrollToBottomInitial,
  themeChangeFromMessageRef,
}: useInitialMessagesProps) => {
  const safetyTimeoutRef = useRef<number | null>(null);
  const recoverEmptyInFlightRef = useRef(false);
  const emptyBootstrapRetriesRef = useRef(0);
  const focus = useAppStore((state) => state.focus);
  const domain = useAppStore((state) => state.domain);
  const deviceConflictModalOpen = useAppStore(
    (state) => state.deviceConflictModalOpen,
  );
  const refreshChat = useAppStore((state) => state.refreshChat);
  const messagesLength = useChatStore((state) => state.messages.length);
  const lastBootstrapAt = useChatStore((state) => state.lastBootstrapAt);
  const isBootstrapping = useChatStore((state) => state.isBootstrapping);

  const resetLoadingState = () => {
    setIsSearchingMessage(false);
  };

  useEffect(() => {
    if (themeChangeFromMessageRef.current) {
      themeChangeFromMessageRef.current = false;
      previousMessageThemeRef.current = messageTheme;
      return;
    }

    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }

    safetyTimeoutRef.current = window.setTimeout(() => {
      resetLoadingState();
    }, 15000);

    const loadInitialMessages = async () => {
      try {
        // Wait for app bootstrap: pagination with empty focus/domain returns [].
        if (!messageTheme || !focus || !domain) {
          resetLoadingState();
          return;
        }

        if (deviceConflictModalOpen || refreshChat || isBootstrapping) {
          resetLoadingState();
          return;
        }

        const { bootstrapChat, messages } = useChatStore.getState();
        const messageThemeChanged =
          previousMessageThemeRef.current !== messageTheme;

        previousMessageThemeRef.current = messageTheme;

        if (messageThemeChanged) {
          setIsSearchingMessage(true);

          if (targetMessageId) {
            setTargetMessageId(null);
          }

          await cleanMessageArray();
          await new Promise((resolve) => setTimeout(resolve, 500));

          try {
            const loaded = await bootstrapChat({ force: true });
            if (
              loaded.length === 0 &&
              emptyBootstrapRetriesRef.current < 2
            ) {
              emptyBootstrapRetriesRef.current += 1;
              await new Promise((resolve) => setTimeout(resolve, 400));
              await bootstrapChat({ force: true });
            }
          } catch (fetchError: any) {
            resetLoadingState();
            return;
          }

          await scrollToBottomInitial(messagesContainerRef.current);
          initialLoadCompletedRef.current = true;
          emptyBootstrapRetriesRef.current = 0;

          resetLoadingState();
          return;
        }

        const shouldLoadInitial =
          !targetMessageId &&
          (!initialLoadCompletedRef.current ||
            (messages.length === 0 && lastBootstrapAt === 0));

        if (shouldLoadInitial) {
          if (messages.length === 0 && recoverEmptyInFlightRef.current) {
            resetLoadingState();
            return;
          }

          if (messages.length === 0) {
            recoverEmptyInFlightRef.current = true;
          }

          try {
            const loaded = await bootstrapChat({
              force: messages.length === 0,
            });
            if (
              loaded.length === 0 &&
              emptyBootstrapRetriesRef.current < 2
            ) {
              emptyBootstrapRetriesRef.current += 1;
              await new Promise((resolve) => setTimeout(resolve, 400));
              await bootstrapChat({ force: true });
            } else if (loaded.length > 0) {
              emptyBootstrapRetriesRef.current = 0;
            }
          } catch (fetchError: any) {
            resetLoadingState();
            return;
          } finally {
            recoverEmptyInFlightRef.current = false;
          }

          await new Promise(requestAnimationFrame);
          await scrollToBottomInitial(messagesContainerRef.current);

          initialLoadCompletedRef.current = true;
          resetLoadingState();
        } else {
          resetLoadingState();
        }
      } catch (error) {
        recoverEmptyInFlightRef.current = false;
        resetLoadingState();
      }
    };

    loadInitialMessages().catch(() => {
      recoverEmptyInFlightRef.current = false;
      resetLoadingState();
    });

    return () => {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }

      setIsSearchingMessage(false);
    };
  }, [
    messageTheme,
    focus,
    domain,
    deviceConflictModalOpen,
    refreshChat,
    messagesLength,
    lastBootstrapAt,
    isBootstrapping,
  ]);
};
